import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { downloadCSV, IMPORT_TEMPLATE, importRowsFromTable, parseCSV, todayISO, WEEKDAYS } from '@/lib/hr'
import type { HRLeaveType, HRSettings } from '@/types/hr'
import { Banner, Field, errMsg } from './shared'

// Off Excel in one go (F-HR-N1): paste the sheet (copy the cells in Excel,
// paste here) or upload a CSV. Opening balances — salary still owed, advances
// still owed, leave already taken — are as of the day Staff & Payroll starts.
// All or nothing: the server checks every row before writing any.

export function ImportDialog({ open, onClose, settings }: { open: boolean; onClose: () => void; settings?: HRSettings }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [text, setText] = useState('')
  const [asOf, setAsOf] = useState('')
  const [serverErrors, setServerErrors] = useState<{ row: number; message: string }[]>([])
  const typesQ = useQuery({ queryKey: ['hr', 'leave-types'], queryFn: () => apiClient.hrListLeaveTypes(), enabled: open })
  const types = (typesQ.data?.data as HRLeaveType[] | undefined) ?? []
  useEffect(() => {
    if (open) {
      setText('')
      setServerErrors([])
      setAsOf(settings?.start_month ? `${settings.start_month}-01` : `${todayISO().slice(0, 7)}-01`)
    }
  }, [open, settings])

  const parsed = useMemo(() => (text.trim() ? importRowsFromTable(parseCSV(text), types) : null), [text, types])

  const run = useMutation({
    mutationFn: (dry: boolean) => apiClient.hrImport({ as_of: asOf, dry_run: dry, rows: parsed!.rows }),
    onSuccess: (res, dry) => {
      setServerErrors([])
      if (dry) {
        toastHelpers.success('Looks good', `${res.data?.rows ?? 0} people ready to import`)
        return
      }
      toastHelpers.success('Imported', `${res.data?.rows ?? 0} people added`)
      qc.invalidateQueries({ queryKey: ['hr'] })
      onClose()
    },
    onError: (err) => {
      const payload = (err as ApiClientError).payload as unknown
      if (Array.isArray(payload)) setServerErrors(payload as { row: number; message: string }[])
      else toastHelpers.error('Import failed', errMsg(err))
    },
  })

  const onFile = async (f: File | undefined) => {
    if (!f) return
    if (/\.xlsx?$/i.test(f.name)) {
      toastHelpers.error('Save it as CSV first', 'In Excel: File → Save As → CSV. Or copy the cells and paste them here.')
      return
    }
    setText(await f.text())
  }

  const rows = parsed?.rows ?? []
  const errors = parsed?.errors ?? []
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import staff from Excel</DialogTitle>
          <DialogDescription>
            Copy the rows from your sheet (with the header row) and paste them below, or upload a CSV. Columns needed: Name, Salary, Joining date. Optional:
            Phone, Designation, Department, Pay type (monthly / daily), Weekly off, Salary due, Advance owed, and one column per leave type (
            {types.map((t) => t.code).join(', ') || 'CL, SL…'}) with days already taken.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <Button variant="outline" size="sm" onClick={() => downloadCSV('staff-import-template.csv', IMPORT_TEMPLATE)}>
            <Download className="mr-1.5 h-4 w-4" />
            Template
          </Button>
          <Input type="file" accept=".csv,.txt,.xlsx,.xls" onChange={(e) => onFile(e.target.files?.[0])} className="h-9 w-64" />
          <Field label="Balances are as of" hint="The day Staff & Payroll starts">
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-9 w-44" />
          </Field>
        </div>
        <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste here" className="font-mono text-xs" />
        {parsed && Object.keys(parsed.columns).length > 0 && (
          <p className="text-xs text-muted-foreground">
            Read as: {Object.entries(parsed.columns).map(([h, f]) => `${h} → ${f.replace(/_/g, ' ')}`).join(' · ')}
          </p>
        )}
        {(errors.length > 0 || serverErrors.length > 0) && (
          <Banner tone="danger">
            <ul className="list-disc space-y-0.5 pl-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
              {serverErrors.map((e) => (
                <li key={`${e.row}-${e.message}`}>
                  Row {e.row + 1} ({rows[e.row - 1]?.full_name ?? '?'}): {e.message}
                </li>
              ))}
            </ul>
          </Banner>
        )}
        {rows.length > 0 && (
          <div className="max-h-72 overflow-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted text-left text-muted-foreground">
                <tr>
                  {['Name', 'Department', 'Pay', 'Joined', 'Off', 'Owed', 'Advance', 'Leave taken'].map((h) => (
                    <th key={h} className="px-2 py-1.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1.5 font-medium">{r.full_name}</td>
                    <td className="px-2 py-1.5">{r.department ?? 'Other'}</td>
                    <td className="px-2 py-1.5 tabular-nums">
                      {r.rate != null ? formatCurrency(r.rate) : '—'} / {/^d/i.test(r.pay_type ?? '') ? 'day' : 'month'}
                    </td>
                    <td className="px-2 py-1.5 tabular-nums">{r.join_date}</td>
                    <td className="px-2 py-1.5">{(r.weekly_off_days ?? []).map((d) => WEEKDAYS[d]).join(', ')}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.opening_due ? formatCurrency(r.opening_due) : ''}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.opening_advance ? formatCurrency(r.opening_advance) : ''}</td>
                    <td className="px-2 py-1.5">
                      {Object.entries(r.leave_used ?? {})
                        .map(([c, d]) => `${c} ${d}`)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => run.mutate(true)} disabled={!rows.length || errors.length > 0 || !asOf || run.isPending}>
            Check
          </Button>
          <Button onClick={() => run.mutate(false)} disabled={!rows.length || errors.length > 0 || !asOf || run.isPending}>
            Import {rows.length || ''} {rows.length === 1 ? 'person' : 'people'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
